<script setup lang="ts">
import { playableYearCount } from '@open-songster/core'
import type { YearBreakdownEntry } from '@open-songster/shared'
import { computed } from 'vue'

/**
 * Trades an even game for a wider spread of years.
 *
 * The usual rule needs a full `tracksPerYear` before a year counts, which
 * throws away almost everything in a playlist spread thinly over decades. The
 * numbers either side of the choice are shown because the trade is only
 * judgeable against them.
 */
interface Props {
  modelValue: boolean
  breakdown: YearBreakdownEntry[]
  tracksPerYear: number
}

const props = defineProps<Props>()
const emit = defineEmits<{ 'update:modelValue': [value: boolean] }>()

/** Years at the strict rule, versus years with anything at all. */
const strictYears = computed(() => playableYearCount(props.breakdown, props.tracksPerYear))
const unevenYears = computed(() => playableYearCount(props.breakdown, 1))

/** Cards that would be scanned with no song behind them. */
const skips = computed(() =>
  props.breakdown
    .filter((entry) => entry.trackCount >= 1)
    .reduce(
      (total, entry) =>
        total + (props.tracksPerYear - Math.min(entry.trackCount, props.tracksPerYear)),
      0,
    ),
)

/** Nothing to trade when every year is already full. */
const wouldChangeAnything = computed(() => unevenYears.value > strictYears.value)
</script>

<template>
  <div class="stack" style="--stack-gap: var(--space-2)">
    <label class="uneven-toggle">
      <input
        type="checkbox"
        :checked="props.modelValue"
        @change="emit('update:modelValue', ($event.target as HTMLInputElement).checked)"
      />
      <span class="stack" style="--stack-gap: var(--space-1)">
        <span class="label">{{ $t('playlist.unevenTitle') }}</span>
        <span class="hint">{{ $t('playlist.unevenHint') }}</span>
      </span>
    </label>

    <p v-if="wouldChangeAnything" class="hint uneven-toggle__effect">
      {{
        $t('playlist.unevenEffect', {
          strict: strictYears,
          uneven: unevenYears,
          skips,
        })
      }}
    </p>
    <p v-else class="hint">{{ $t('playlist.unevenNoEffect') }}</p>
  </div>
</template>

<style scoped>
.uneven-toggle {
  display: grid;
  grid-template-columns: auto 1fr;
  gap: var(--space-3);
  align-items: start;
  cursor: pointer;
}

.uneven-toggle input {
  margin-top: 0.2rem;
}

.uneven-toggle__effect {
  color: var(--color-text);
}
</style>
